import type { DataTableColumnDef } from 'components/tables/data-table';
import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { useMemo } from 'react';
import { useLazyGetProductivityByEmployeeReportQuery } from 'store/api/reportingPerformance';
import { ProductivityByEmployeeRow, ProductivityByEmployeeTotals } from 'types/reporting/performance';
import { ReportPage } from 'types/reporting/reportPage';
import { t } from 'utils/getTranslations';

import {
    employeeLabel,
    ReportEmployeeLink,
    ReportNumberCell,
    TONED_COLUMN_META,
    tonedCell,
    ToneRule,
    GREEN,
    YELLOW,
    RED,
} from '../reportCells';
import {
    employeeFilter,
    optionsFilter,
    periodFilter,
    periodQuickFilter,
    quickFiltersFrom,
    searchSelectors,
    yesNoFilter,
} from '../reportFilters';
import { ReportLayout } from '../ReportLayout';
import { toneLegendOf } from '../ReportToneLegend';
import { ReportRowLimitNote } from '../ReportRowLimitNote';
import { ReportEntry } from '../reportCatalog';
import { inBaseCurrency, useReportBaseCurrency } from '../reportCurrency';

type Row = ProductivityByEmployeeRow;
type Totals = ProductivityByEmployeeTotals;
type Figure = Exclude<keyof Totals, 'baseCurrency'>;
/** The multiplier is a ratio per employee: it has no total, and four decimals keep a small discount visible. */
const MULTIPLIER_DECIMALS = 4;

const REPORT_KEY = 'productivity-by-employee';
/** The work categories the backend sorts every timecard into, with their labels. */
const WORK_CATEGORIES = [
    { value: 'CLIENT', labelId: 'reporting.filter.work-category-client' },
    { value: 'PRO_BONO', labelId: 'reporting.filter.work-category-pro-bono' },
    { value: 'INTERNAL', labelId: 'reporting.filter.work-category-internal' },
    { value: 'OFFERS', labelId: 'reporting.filter.work-category-offers' },
];

function buildFilterConfig(): FilterConfig {
    const filters: MainFilter[] = [
        periodFilter('reporting.filter.timecard-date-range'),
        optionsFilter(
            'workCategory',
            'reporting.filter.work-category',
            WORK_CATEGORIES.map(({ value, labelId }) => ({ label: t(labelId), value }))
        ),
        employeeFilter('employeeId', 'reporting.filter.employee'),
        yesNoFilter('employeeIsActive', 'reporting.filter.is-active'),
        yesNoFilter('hasWip', 'reporting.filter.has-wip'),
    ];
    return {
        hideFilterDividers: true,
        quickFilterChips: true,
        periodQuickFilter: periodQuickFilter({ defaultPeriod: 'last-month' }),
        quickFilters: quickFiltersFrom(filters, {
            workCategory: 'reporting.filter.work-category',
            employeeIsActive: 'reporting.filter.is-active',
            hasWip: 'reporting.filter.has-wip',
        }),
        searchBarSelectors: searchSelectors('employeeName'),
        filters,
    };
}

function figureColumn(
    id: Figure,
    header: string,
    rows: Row[] | undefined,
    rule?: ToneRule,
    widthWeight = 0.7
): DataTableColumnDef<Row, Totals> {
    return {
        id,
        accessorKey: id,
        header,
        meta: {
            ...(rule && TONED_COLUMN_META),
            widthWeight,
            minPx: 120,
            textAlign: 'right',
            nullHandling: 'nullsLast',
            renderTotal: (totals) => <ReportNumberCell value={totals?.[id]} />,
        },
        cell: rule
            ? tonedCell(rows, (row) => row[id], rule)
            : ({ row }) => <ReportNumberCell value={row.original[id]} />,
    };
}

function buildColumns(rows: Row[] | undefined, currency: string | null): DataTableColumnDef<Row, Totals>[] {
    const amount = (headerId: string) => inBaseCurrency(t(headerId), currency);
    return [
        {
            id: 'employeeName',
            accessorKey: 'employeeName',
            header: t('reporting.column.employee'),
            meta: { widthWeight: 1.2, minPx: 160 },
            cell: ({ row }) => (
                <ReportEmployeeLink employeeId={row.original.employeeId}>
                    {employeeLabel(row.original)}
                </ReportEmployeeLink>
            ),
        },
        {
            id: 'jobRole',
            accessorKey: 'jobRole',
            header: t('reporting.column.job-role'),
            meta: { widthWeight: 1, minPx: 140 },
        },
        figureColumn('usedHours', t('reporting.column.used-hours'), rows),
        figureColumn('nonBillableHours', t('reporting.column.non-billable-hours'), rows),
        figureColumn('billableHours', t('reporting.column.billable-hours'), rows, YELLOW),
        figureColumn('billableAmount', amount('reporting.column.billable-amount'), rows, YELLOW, 0.8),
        figureColumn('wipHours', t('reporting.column.wip-hours'), rows, RED),
        figureColumn('wipAmount', amount('reporting.column.wip-amount'), rows, RED, 0.8),
        figureColumn('invoicedHours', t('reporting.column.invoiced-hours'), rows, GREEN),
        figureColumn('invoicedAmount', amount('reporting.column.invoiced-amount'), rows, GREEN, 0.8),
        {
            id: 'billableEffectiveMultiplier',
            accessorKey: 'billableEffectiveMultiplier',
            header: t('reporting.column.billable-effective-multiplier'),
            meta: { widthWeight: 0.8, minPx: 140, textAlign: 'right', nullHandling: 'nullsLast' },
            cell: ({ row }) => (
                <ReportNumberCell value={row.original.billableEffectiveMultiplier} decimals={MULTIPLIER_DECIMALS} />
            ),
        },
    ];
}

export function ProductivityByEmployeeReport({ report }: { report: ReportEntry }) {
    const [fetchData, { data, isFetching, isError }] = useLazyGetProductivityByEmployeeReportQuery();
    const filterConfig = useMemo(() => buildFilterConfig(), []);
    const currency = useReportBaseCurrency(data?.totals?.baseCurrency ?? data?.content[0]?.baseCurrency);
    const columns = useMemo(() => buildColumns(data?.content, currency), [data, currency]);
    return (
        <ReportLayout report={report} fillHeight toneLegend={toneLegendOf(columns)} baseCurrency={currency}>
            <ListTableWithFilters<Row, ReportPage<Row, Totals>>
                data={data}
                belowQuickFilters={<ReportRowLimitNote report={report} rowCount={data?.content.length} />}
                isLoading={isFetching}
                isError={isError}
                fetchData={fetchData}
                filterConfig={filterConfig}
                columns={columns}
                columnConfigId={`report-${REPORT_KEY}`}
                enableColumnPicker
                enableColumnReorder
                enableColumnResize
                pluralTextId="reporting.employees"
                singularTextId="reporting.employee"
                noEntriesId="reporting.no-rows"
                noEntriesBodyId="reporting.no-rows-body"
            />
        </ReportLayout>
    );
}
